
import { IGetDocumentsParams, IDocumentDownloadRequestObject } from './../../../models/document';
import { Injectable } from '@angular/core';
import { combineLatest } from "rxjs";
import { IGeneralSelectionState } from '../../state/general-selection.state';
import { IAppState } from '../../state/app.state';
import { Store, select } from '@ngrx/store';
import { selectDocumentLibraryState } from '../selectors/document-library.selectors';
import { IDocumentLibraryState } from '../../state/document-library.state';
import { areArraysUnequal } from 'src/app/utils/comparative.methods';
import * as DocumentLibraryActions from '../actions/document-library.actions';
import { GeneralSelectionFacade } from '../../general-selection/facade/general-selection.facade';
import { PortfoliosFacade } from "../../portfolios/facade/portfolios.facade";
import { switchMap } from "rxjs/operators";
import { DocumentLibraryService } from "../../../services/http/document-library.service";
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';

@UntilDestroy()
@Injectable()
export class DocumentLibraryFacade {

    
    public productDisclosureStatements$ =
        this.generalSelectionFacade.getSelectedEntityPortfolioState()
            .pipe(
        switchMap(generalState => {
            let portfolioIds = generalState.selectedPortfolios.map(p => p.id);

            if (portfolioIds.length === 0) {
                return [];
            }

            return this.documentService.getProductDisclosureDocuments(portfolioIds);
        }));

    public termsAndConditionDocuments$ =
        this.generalSelectionFacade.getSelectedEntityPortfolioState()
            .pipe(
        switchMap(generalState => {
            let portfolioIds = generalState.selectedPortfolios.map(p => p.id);

            if (portfolioIds.length === 0) {
                return [];
            }

            return this.documentService.getTermsAndConditionDocuments(portfolioIds);
        }));
    
    public documentLibraryState$ = this.store.pipe(select(selectDocumentLibraryState));
    private generalSelectionState: IGeneralSelectionState;
    private documentLibraryState: IDocumentLibraryState;
    private isDataLoading = false;

    constructor(
        private store: Store<IAppState>, 
        private generalSelectionFacade: GeneralSelectionFacade, 
        private portfolioFacade: PortfoliosFacade, 
        private documentService: DocumentLibraryService) {
        this.initialiseData();
    }

    initialiseData() {
        this.subscribeToDocumentLibraryState();
        this.subscribeToGeneralSelection();
    }


    private subscribeToGeneralSelection() {
        this.store.select('generalSelection')
        .pipe(
            untilDestroyed(this)
        )
        .subscribe((state: IGeneralSelectionState) => {
            this.generalSelectionState = state;
            if (!this.isDataLoading && this.generalSelectionState.selectedEntities.length > 0) {
                this.getDocuments();
            }
        });
    }

    private subscribeToDocumentLibraryState() {
        this.documentLibraryState$
        .pipe(
            untilDestroyed(this)
        )
        .subscribe((documentLibraryState: IDocumentLibraryState) => {
            if (documentLibraryState) {
                this.isDataLoading = documentLibraryState.loading;
                this.documentLibraryState = documentLibraryState;
            }
        });
    }

    public getDocuments() {
        const isChanged: boolean = this.ifGeneralSelectionStateChanged(this.documentLibraryState, this.generalSelectionState);
        if (isChanged) {
            this.resetDocuments();
            const param: IGetDocumentsParams = {
                toDate: this.generalSelectionState.toDate,
                entityIds: this.generalSelectionState.selectedEntities,
            };
            this.store.dispatch(new DocumentLibraryActions.GetDocuments(param));
        }
    }

    private resetDocuments() {
        this.store.dispatch(new DocumentLibraryActions.ResetDocuments());
    }

    private ifGeneralSelectionStateChanged(documentLibraryState: IDocumentLibraryState, generalSelectionState: IGeneralSelectionState): boolean {

        let generalSelectionStateChanged = false;
        if (!this.documentLibraryState.loading) {
            if (generalSelectionState && generalSelectionState.selectedEntities && generalSelectionState.selectedEntities.length > 0 && !!generalSelectionState.toDate) {
                if (documentLibraryState.clientEntityIds.length !== generalSelectionState.selectedEntities.length) {
                    generalSelectionStateChanged = true;
                } else {
                    generalSelectionStateChanged = areArraysUnequal(documentLibraryState.clientEntityIds, generalSelectionState.selectedEntities, 'EntityId');
                }
            }
        }
        return generalSelectionStateChanged;
    }

    public downloadDocument(requestObject: IDocumentDownloadRequestObject) {
        this.store.dispatch(new DocumentLibraryActions.DownloadDocument(requestObject));
    }

    private getPortfolioIds(
        selectedPortfolios,
        portfoliosOfSelectedEntities,
    ): number[] {
        return selectedPortfolios.length > 0
            ? selectedPortfolios.map((x) => x.id)
            : portfoliosOfSelectedEntities.map((x) => x.id);
    }

}
